import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dahc923p14a1h3mr6id0';

export default function SubscribeSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('订阅成功，明天早上甜瓜日报就会到你的邮箱。');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    setStatus('loading');
    setMessage('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setMessage('订阅成功，明天早上甜瓜日报就会到你的邮箱。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '订阅暂时没有成功，请稍后再试一次。');
      }
    } catch {
      setStatus('error');
      setMessage('网络好像走神了，请稍后再试一次。');
    }
  };

  return (
    <section id="subscribe" className="w-full px-4 md:px-8 py-14 md:py-20">
      <div className="relative overflow-hidden rounded-2xl border border-background-200 bg-background-100">
        <div
          className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-accent-100 blur-3xl opacity-70"
          aria-hidden="true"
        ></div>
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 p-6 md:p-12">
          <div>
            <p className="flex items-center gap-2 text-xs tracking-widest text-primary-600">
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              SWEET DAILY
            </p>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl text-foreground-950 leading-snug">
              订阅甜瓜日报
              <br />
              每天 3 条好心情
            </h2>
            <p className="mt-4 max-w-md text-sm md:text-base text-foreground-700 leading-relaxed">
              工作日上午发一封邮件，只装当天最甜的三条瓜料和一句小感想。不推广告、不分享邮箱，随时可以退订。
            </p>
            <ul className="mt-6 space-y-3 text-sm text-foreground-700">
              {['每条瓜都由编辑先读完再推荐', '只推甜向内容，绝不掺焦虑', '订阅与退订都只需一次点击'].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <i className="ri-check-line text-primary-500" aria-hidden="true"></i>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-7">
            <form
              id="sweet-daily-subscribe-form"
              data-readdy-form
              onSubmit={handleSubmit}
              encType="application/x-www-form-urlencoded"
            >
              <div>
                <label htmlFor="subscribe-email" className="block text-sm text-foreground-800">
                  邮箱地址
                </label>
                <input
                  id="subscribe-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="mt-2 w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-300"
                />
              </div>

              <div className="mt-4">
                <label htmlFor="subscribe-topic" className="block text-sm text-foreground-800">
                  最想看哪一类甜瓜
                </label>
                <select
                  id="subscribe-topic"
                  name="topic"
                  defaultValue="love-notes"
                  className="mt-2 w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-300"
                >
                  <option value="love-notes">恋爱小料</option>
                  <option value="heartbeat">心动瞬间</option>
                  <option value="official">官宣现场</option>
                  <option value="couple-things">情侣小事</option>
                </select>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="input-extra-note"
              />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-6 w-full rounded-full bg-primary-500 px-6 py-3 text-sm text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {status === 'loading' ? '正在订阅…' : '订阅甜瓜日报'}
              </button>

              {status !== 'idle' && message && (
                <p
                  className={`mt-4 rounded-lg px-3.5 py-2.5 text-sm ${
                    status === 'success'
                      ? 'bg-accent-100 text-accent-900'
                      : status === 'error'
                        ? 'bg-primary-100 text-primary-800'
                        : 'bg-background-100 text-foreground-600'
                  }`}
                >
                  {message}
                </p>
              )}
              <p className="mt-3 text-xs text-foreground-500">我们只会用这个邮箱发送甜瓜日报，绝不外传。</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}